import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Descriptions, 
  Tag, 
  Divider, 
  Spin, 
  message, 
  Alert,
  Space,
  Empty,
  Statistic,
  Tabs
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  FlagOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  MessageOutlined,
  PaperClipOutlined,
  SettingOutlined,
  CommentOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  StopOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { 
  SubtaskDetailProps, 
  PlanogramSubtask, 
  SubtaskComment, 
  SubtaskAttachment,
  SubtaskStatus,
} from '../../types/plan-sub-task.ts';
import { STATUS_ACTIONS } from '../../types/plan-sub-task';
import { PlanogramSubtaskService } from '../../services/planogramSubtaskService.js';
import StatusTag from './components/StatusTag';
import CommentSection from './components/CommentSection';
import AttachmentManager from './components/AttachmentManager';
import StatusHistory from './components/StatusHistory';

const { Title, Text, Paragraph } = Typography;

/**
 * Subtask Detail Page Component
 * Displays comprehensive task information including basic info, execution notes, 
 * attachments, comments, and status history
 */
const SubtaskDetail: React.FC<SubtaskDetailProps> = ({ subtaskCode, onBack, onStatusChange }) => {
  // Navigation hook
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State management
  const [subtask, setSubtask] = useState<PlanogramSubtask | null>(null);
  const [comments, setComments] = useState<SubtaskComment[]>([]);
  const [attachments, setAttachments] = useState<SubtaskAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // 新增状态：跟踪评论是否已加载
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  // 新增状态：跟踪附件是否已加载
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
  // 新增状态：跟踪当前激活的tab
  const [activeTab, setActiveTab] = useState('basic');

  // Service instance
  const subtaskService = PlanogramSubtaskService.getInstance();

  // Load subtask data
  const loadSubtaskData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 只加载子任务详情，不加载评论和附件
      const subtaskResponse = await subtaskService.getSubtaskByCode(subtaskCode);

      if (subtaskResponse.success && subtaskResponse.data) {
        setSubtask(subtaskResponse.data);
      } else {
        setError(subtaskResponse.errorMessage || t('subtask.detail.loadSubtaskDetailFailed'));
        return;
      }
    } catch (error) {
      console.error('Error loading subtask data:', error);
      setError(t('subtask.detail.loadSubtaskDetailFailed'));
    } finally {
      setLoading(false);
    }
  }, [subtaskService, subtaskCode]);

  // 新增：专门加载评论数据的方法
  const loadCommentsData = useCallback(async () => {
    if (commentsLoaded) return; // 如果已经加载过，直接返回
    
    try {
      const commentsResponse = await subtaskService.getComments(subtaskCode);
      
      if (commentsResponse.success) {
        setComments(commentsResponse.data || []);
        setCommentsLoaded(true); // 标记为已加载
      }
    } catch (error) {
      console.error('Error loading comments data:', error);
      message.error(t('subtask.detail.loadCommentsFailed'));
    }
  }, [subtaskService, subtaskCode, commentsLoaded]);

  // 新增：专门加载附件数据的方法
  const loadAttachmentsData = useCallback(async () => {
    if (attachmentsLoaded) return; // 如果已经加载过，直接返回
    
    try {
      const attachmentsResponse = await subtaskService.getAttachments(subtaskCode);
      
      if (attachmentsResponse.success) {
        setAttachments(attachmentsResponse.data || []);
        setAttachmentsLoaded(true); // 标记为已加载
      }
    } catch (error) {
      console.error('Error loading attachments data:', error);
      message.error(t('subtask.detail.loadAttachmentsFailed'));
    }
  }, [subtaskService, subtaskCode, attachmentsLoaded]);

  // Initial data loading
  useEffect(() => {
    loadSubtaskData();
  }, [loadSubtaskData]);

  // 新增：监听tab切换，当切换到评论tab时加载评论数据
  useEffect(() => {
    if (activeTab === 'comments') {
      loadCommentsData();
    }
  }, [activeTab, loadCommentsData]);

  // 新增：监听tab切换，当切换到附件tab时加载附件数据
  useEffect(() => {
    if (activeTab === 'attachments') {
      loadAttachmentsData();
    }
  }, [activeTab, loadAttachmentsData]);

  // Handle status change (will be used by TaskActions component in task 4.2)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStatusUpdate = async (newStatus: SubtaskStatus) => {
    if (!subtask) return;

    setActionLoading(true);
    try {
      const response = await subtaskService.updateSubtaskStatus(subtaskCode, newStatus);
      
      if (response.success && response.data) {
        setSubtask(response.data);
        message.success(t('subtask.detail.statusUpdateSuccess'));
        onStatusChange(newStatus);
      } else {
        message.error(response.errorMessage || t('subtask.detail.statusUpdateFailed'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error(t('subtask.detail.statusUpdateFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status icon
  const getStatusIcon = (status: SubtaskStatus) => {
    switch (status) {
      case 'Pending':
        return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
      case 'Active':
        return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      case 'Ongoing':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'Finished':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'Cancelled':
        return <StopOutlined style={{ color: '#ff4d4f' }} />;
      case 'Paused':
        return <PauseCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // Render breadcrumb navigation
  const renderBreadcrumb = () => {
    return (
      <div style={{ marginBottom: 0 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ 
            padding: 0, 
            height: 'auto',
            color: 'rgba(255,255,255,0.9)',
            fontSize: '14px'
          }}
        >
          {t('subtask.detail.backToList')}
        </Button>
      </div>
    );
  };

  // Render page header
  const renderPageHeader = () => {
    if (!subtask) return null;

    // Get available actions based on status
    const availableActions = STATUS_ACTIONS[subtask.status] || ['view'];

    return (
      <div style={{ 
        backgroundColor: '#fafafa', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '16px' }}>
              <Title level={2} style={{ margin: 0, color: '#1f2937', marginBottom: '8px' }}>
                {subtask.subtaskName}
              </Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {t('subtask.detail.taskCode')}：{subtask.subtaskCode}
              </Text>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>{t('subtask.detail.status')}：</Text>
                <StatusTag status={subtask.status} overdueFlag={subtask.overdueFlag} />
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>{t('subtask.detail.assignee')}：</Text>
                <UserOutlined style={{ color: '#1890ff' }} />
                <Text style={{ fontWeight: 500 }}>{subtask.assignee}</Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>{t('subtask.detail.parentMilestone')}：</Text>
                <Text style={{ fontWeight: 500 }}>{subtask.milestoneName}</Text>
              </div>
            </div>
          </div>
          
          <Space size="middle">
            {/* 开始任务按钮 */}
            {availableActions.includes('start') && (
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                loading={actionLoading}
                onClick={handleStartTask}
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px 0 rgba(24, 144, 255, 0.2)'
                }}
              >
                {t('subtask.detail.startTask')}
              </Button>
            )}
            
            {/* 完成任务按钮 */}
            {availableActions.includes('complete') && (
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                loading={actionLoading}
                onClick={() => navigate(`/subtasks/${subtask.subtaskCode}/complete`)}
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px 0 rgba(24, 144, 255, 0.2)'
                }}
              >
                {t('subtask.detail.completeTask')}
              </Button>
            )}
            
            <Button
              size="large"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('subtask.detail.backToTaskList')}
            </Button>
          </Space>
        </div>
      </div>
    );
  };

  // Render basic information section
  const renderBasicInfo = () => {
    if (!subtask) return null;

    return (
      <div>
        {/* 详细信息 */}
        <Descriptions 
          column={{ xs: 1, sm: 2, md: 2, lg: 2 }} 
          bordered={false}
          size="middle"
          labelStyle={{ 
            fontWeight: 500, 
            color: '#595959',
            width: '140px'
          }}
          contentStyle={{ 
            color: '#262626',
            fontWeight: 400
          }}
        >
          <Descriptions.Item label={t('subtask.detail.parentMilestone')}>
            <Text>{subtask.milestoneName}</Text>
          </Descriptions.Item>
          {/* <Descriptions.Item label={t('subtask.detail.milestoneOwner')}>
            <Space>
              <UserOutlined style={{ color: '#1890ff' }} />
              <Text>{subtask.milestoneOwner}</Text>
            </Space>
          </Descriptions.Item> */}
          <Descriptions.Item label={t('subtask.detail.creator')}>
            <Text>{subtask.creatorName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('subtask.detail.createTime')}>
            <Space>
              <CalendarOutlined style={{ color: '#52c41a' }} />
              <Text>{formatDate(subtask.gmtCreate)}</Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Divider style={{ margin: '24px 0' }} />

        {/* 时间信息 */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ fontSize: '16px', color: '#262626' }}>
            <ClockCircleOutlined style={{ marginRight: '8px', color: '#faad14' }} />
            {t('subtask.detail.timeNodes')}
          </Text>
        </div>
        
        <Descriptions 
          column={{ xs: 1, sm: 2, md: 2 }} 
          bordered={false}
          size="middle"
          labelStyle={{ 
            fontWeight: 500, 
            color: '#595959',
            width: '140px'
          }}
          contentStyle={{ 
            color: '#262626',
            fontWeight: 400
          }}
        >
          <Descriptions.Item label={t('subtask.detail.milestoneStart')}>
            <Space>
              <FlagOutlined style={{ color: '#1890ff' }} />
              <Text>{formatDate(subtask.milestoneStartDate)}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('subtask.detail.dueTime')}>
            <Space>
              <CalendarOutlined style={{ color: subtask.overdueFlag ? '#ff4d4f' : '#faad14' }} />
              <Text style={{ 
                color: subtask.overdueFlag ? '#ff4d4f' : undefined,
                fontWeight: subtask.overdueFlag ? 600 : 400
              }}>
                {formatDate(subtask.dueDate)}
              </Text>
              {subtask.overdueFlag && (
                 <Tag color="red" style={{ marginLeft: '8px', fontSize: '12px' }}>
                   {t('subtask.detail.overdue')}
                 </Tag>
               )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('subtask.detail.actualStart')}>
            <Space>
              <ClockCircleOutlined style={{ color: '#52c41a' }} />
              <Text>{formatDate(subtask.startDate)}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('subtask.detail.completionTime')}>
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text>{formatDate(subtask.completionDate)}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('subtask.detail.planEnd')}>
            <Space>
              <CalendarOutlined style={{ color: '#722ed1' }} />
              <Text>{formatDate(subtask.planEndDate)}</Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Divider style={{ margin: '24px 0' }} />

        {/* 执行说明 */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ fontSize: '16px', color: '#262626' }}>
            <MessageOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
            {t('subtask.detail.remarks')}
          </Text>
        </div>
        
        {subtask.remarks ? (
          <div style={{
            background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9e8 100%)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #d9f7be',
            position: 'relative',
            marginBottom: '24px'
          }}>
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              color: '#52c41a',
              fontSize: '24px',
              opacity: 0.3
            }}>
              <MessageOutlined />
            </div>
            <Paragraph style={{ 
              margin: 0, 
              fontSize: '15px', 
              lineHeight: '1.6',
              color: '#262626'
            }}>
              {subtask.remarks}
            </Paragraph>
          </div>
        ) : (
          <div style={{
            background: '#fafafa',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px dashed #d9d9d9',
            marginBottom: '24px'
          }}>
            <MessageOutlined style={{ 
              fontSize: '32px', 
              color: '#d9d9d9', 
              marginBottom: '12px' 
            }} />
            <div style={{ color: '#8c8c8c', fontSize: '14px' }}>
              {t('subtask.detail.noRemarks')}
            </div>
            <div style={{ color: '#bfbfbf', fontSize: '12px', marginTop: '4px' }}>
              {t('subtask.detail.remarksWillBeAdded')}
            </div>
          </div>
        )}
        
        {/* 交付物 */}
        {subtask.deliverables && typeof subtask.deliverables === 'string' && subtask.deliverables.length > 0 && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                <PaperClipOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
                {t('subtask.detail.deliverables')}
              </Text>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #fff7e6 0%, #fef3e2 100%)',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #ffd591'
            }}>
              <div style={{
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#262626'
              }}>
                {subtask.deliverables}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };



  // Handle upload attachment
  const handleUploadAttachment = async (file: File) => {
    try {
      const response = await subtaskService.uploadAttachment(subtaskCode, file);
      
      if (response.success && response.data) {
        // Add new attachment to the list
        setAttachments(prev => [response.data, ...prev]);
        // 更新附件加载状态
        if (!attachmentsLoaded) {
          setAttachmentsLoaded(true);
        }
      } else {
        throw new Error(response.errorMessage || t('subtask.detail.uploadAttachmentFailed'));
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  };

  // Handle delete attachment
  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      const response = await subtaskService.deleteAttachment(attachmentId);
      
      if (response.success) {
        // Remove attachment from the list
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      } else {
        throw new Error(response.errorMessage || t('subtask.detail.deleteAttachmentFailed'));
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  };

  // Render attachments section
  const renderAttachments = () => (
    <AttachmentManager
      subtaskCode={subtaskCode}
      attachments={attachments}
      onUpload={handleUploadAttachment}
      onDelete={handleDeleteAttachment}
      loading={loading}
      showCard={false}
    />
  );

  // Handle add comment
  const handleAddComment = async (content: string) => {
    try {
      const response = await subtaskService.addComment(subtaskCode, content);
      
      if (response.success && response.data) {
        // Add new comment to the list
        setComments(prev => [response.data, ...prev]);
      } else {
        throw new Error(response.errorMessage || t('subtask.detail.addCommentFailed'));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  // Render comments section
  const renderComments = () => (
    <CommentSection
      subtaskCode={subtaskCode}
      comments={comments}
      onAddComment={handleAddComment}
      loading={loading}
      showCard={false}
    />
  );

  // Render status history section
  const renderStatusHistory = () => {
    if (!subtask) return null;

    return (
      <div>
        <StatusHistory statusHistory={subtask.statusChangeHistory} />
      </div>
    );
  };

  // Handle start task
  const handleStartTask = async () => {
    if (!subtask) return;

    setActionLoading(true);
    try {
      const response = await subtaskService.startSubtask(subtask.id, subtaskCode);
      
      if (response.success && response.data) {
        setSubtask(response.data);
        message.success(t('subtask.detail.taskStarted'));
        onStatusChange('Ongoing');
      } else {
        message.error(response.errorMessage || t('subtask.detail.startTaskFailed'));
      }
    } catch (error) {
      console.error('Error starting task:', error);
      message.error(t('subtask.detail.startTaskFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // 修改Tabs组件的onChange处理函数
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div style={{ 
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #e5e7eb',
          padding: '24px 32px'
        }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ 
              padding: 0, 
              height: 'auto',
              color: '#666',
              fontSize: '14px',
              marginBottom: '16px'
            }}
          >
            {t('subtask.detail.backToList')}
          </Button>
          <Title level={2} style={{ 
            margin: 0, 
            color: '#1f2937'
          }}>
            {t('subtask.detail.loading')}
          </Title>
        </div>
        
        <div style={{ 
          padding: '64px 24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div style={{ 
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #e5e7eb',
          padding: '24px 32px'
        }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ 
              padding: 0, 
              height: 'auto',
              color: '#666',
              fontSize: '14px',
              marginBottom: '16px'
            }}
          >
            {t('subtask.detail.backToList')}
          </Button>
          <Title level={2} style={{ 
            margin: 0, 
            color: '#1f2937'
          }}>
            {t('subtask.detail.loadFailed')}
          </Title>
        </div>
        
        <div style={{ 
          padding: '24px'
        }}>
          <Alert
            message={t('subtask.detail.loadSubtaskDetailFailed')}
            description={error}
            type="error"
            showIcon
            action={
              <Button type="primary" onClick={loadSubtaskData}>
                {t('subtask.detail.reload')}
              </Button>
            }
            style={{ 
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}
          />
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      {renderPageHeader()}

      {/* Main Content Area */}
      <div style={{ 
        padding: '24px'
      }}>
        <Spin spinning={actionLoading}>
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              background: '#fff'
            }}
            bodyStyle={{ padding: 0 }}
          >
            <Tabs
              defaultActiveKey="basic"
              size="large"
              style={{ padding: '0 24px' }}
              tabBarStyle={{ 
                marginBottom: 0,
                borderBottom: '1px solid #f0f0f0'
              }}
              // 添加onChange事件处理
              onChange={handleTabChange}
              items={[
                {
                  key: 'basic',
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileTextOutlined />
                      {t('subtask.detail.tabs.basicInfo')}
                    </span>
                  ),
                  children: (
                    <div style={{ padding: '24px' }}>
                      {renderBasicInfo()}
                    </div>
                  )
                },

                // {
                //   key: 'attachments',
                //   label: (
                //     <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                //       <PaperClipOutlined />
                //       {t('subtask.detail.tabs.attachments')}
                //     </span>
                //   ),
                //   children: (
                //     <div style={{ padding: '24px' }}>
                //       {renderAttachments()}
                //     </div>
                //   )
                // },
                // {
                //   key: 'comments',
                //   label: (
                //     <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                //       <CommentOutlined />
                //       {t('subtask.detail.tabs.comments')}
                //     </span>
                //   ),
                //   children: (
                //     <div style={{ padding: '24px' }}>
                //       {renderComments()}
                //     </div>
                //   )
                // },
                // {
                //   key: 'history',
                //   label: (
                //     <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                //       <HistoryOutlined />
                //       {t('subtask.detail.tabs.history')}
                //     </span>
                //   ),
                //   children: (
                //     <div style={{ padding: '24px' }}>
                //       {renderStatusHistory()}
                //     </div>
                //   )
                // }
              ]}
            />
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default SubtaskDetail;